import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash, Edit, Save, XCircle } from "lucide-react";
import { createAgentVariable, deleteAgentVariable, updateAgentVariable } from "@/services/admin/agentVariableService";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { AgentVariable } from "@/types/admin";

const variableFormSchema = z.object({
  key: z.string().min(1, "Key is required"),
  value: z.string().min(1, "Value is required"),
  source: z.enum(["admin", "dealer", "system"]),
});

type VariableFormValues = z.infer<typeof variableFormSchema>;

interface AgentVariablesProps {
  agentId: string;
  variables: AgentVariable[];
  onSuccess: () => void;
}

export const AgentVariables = ({ agentId, variables, onSuccess }: AgentVariablesProps) => {
  const [editingVariable, setEditingVariable] = useState<AgentVariable | null>(null);
  const [isAddingVariable, setIsAddingVariable] = useState(false);

  const form = useForm<VariableFormValues>({
    resolver: zodResolver(variableFormSchema),
    defaultValues: {
      key: "",
      value: "",
      source: "admin",
    },
  });

  const createVariableMutation = useMutation({
    mutationFn: (data: VariableFormValues) => createAgentVariable({
      agent_id: agentId,
      key: data.key,
      value: data.value,
      source: data.source
    }),
    onSuccess: () => {
      form.reset();
      setIsAddingVariable(false);
      toast({
        title: "Variable added",
        description: "The variable has been added successfully.",
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to add variable: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const updateVariableMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { value?: string; source?: string } }) => 
      updateAgentVariable(id, data),
    onSuccess: () => {
      setEditingVariable(null);
      toast({
        title: "Variable updated",
        description: "The variable has been updated successfully.",
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update variable: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const deleteVariableMutation = useMutation({
    mutationFn: deleteAgentVariable,
    onSuccess: () => {
      toast({
        title: "Variable deleted",
        description: "The variable has been deleted successfully.",
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete variable: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: VariableFormValues) => {
    createVariableMutation.mutate(data);
  };

  const handleEditClick = (variable: AgentVariable) => {
    setEditingVariable(variable);
    form.setValue("key", variable.key);
    form.setValue("value", variable.value || "");
    form.setValue("source", variable.source as "admin" | "dealer" | "system");
  };

  const handleSaveEdit = () => {
    if (!editingVariable) return;
    
    const data = {
      value: form.getValues("value"),
      source: form.getValues("source") as "admin" | "dealer" | "system",
    };
    
    updateVariableMutation.mutate({
      id: editingVariable.id,
      data,
    });
  };

  const handleCancelEdit = () => {
    setEditingVariable(null);
    form.reset();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this variable?")) {
      deleteVariableMutation.mutate(id);
    }
  };

  const sourceBadgeColor = (source: string) => {
    switch (source) {
      case "admin":
        return "bg-blue-100 text-blue-800";
      case "dealer":
        return "bg-green-100 text-green-800";
      case "system":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Agent Variables</h3>
        <Button 
          size="sm" 
          onClick={() => setIsAddingVariable(true)}
          disabled={isAddingVariable || !!editingVariable}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Variable
        </Button>
      </div>

      {isAddingVariable && (
        <div className="p-4 border rounded-md bg-muted/5">
          <h4 className="font-medium mb-3">Add New Variable</h4>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Key</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., dealership_name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Value</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Roy Nichols Motors" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="dealer">Dealer</SelectItem>
                        <SelectItem value="system">System</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddingVariable(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createVariableMutation.isPending}>
                  {createVariableMutation.isPending ? "Adding..." : "Add Variable"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}

      {variables.length > 0 ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variables.map((variable) => (
                <TableRow key={variable.id}>
                  <TableCell className="font-medium">{variable.key}</TableCell>
                  <TableCell>
                    {editingVariable?.id === variable.id ? (
                      <Input
                        value={form.getValues("value")}
                        onChange={(e) => form.setValue("value", e.target.value)}
                        className="w-full"
                      />
                    ) : (
                      variable.value || "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {editingVariable?.id === variable.id ? (
                      <Select
                        value={form.getValues("source")}
                        onValueChange={(value) => form.setValue("source", value as "admin" | "dealer" | "system")}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue placeholder="Source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="dealer">Dealer</SelectItem>
                          <SelectItem value="system">System</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={sourceBadgeColor(variable.source)}>
                        {variable.source}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {editingVariable?.id === variable.id ? (
                        <>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={handleCancelEdit}
                          >
                            <XCircle className="h-4 w-4" />
                            <span className="sr-only">Cancel</span>
                          </Button>
                          <Button 
                            variant="default" 
                            size="icon" 
                            onClick={handleSaveEdit}
                            disabled={updateVariableMutation.isPending}
                          >
                            <Save className="h-4 w-4" />
                            <span className="sr-only">Save</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditClick(variable)}
                            disabled={!!editingVariable || isAddingVariable}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDelete(variable.id)}
                            disabled={!!editingVariable || isAddingVariable || deleteVariableMutation.isPending}
                          >
                            <Trash className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-8 border rounded-md bg-muted/10">
          <p className="text-muted-foreground mb-4">No variables defined yet.</p>
          <Button onClick={() => setIsAddingVariable(true)} disabled={isAddingVariable}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Variable
          </Button>
        </div>
      )}
    </div>
  );
};
